import { Directive, ElementRef, Input, OnInit, Renderer2, NgZone, OnDestroy, OnChanges } from '@angular/core';

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: '[appResizable]',
	standalone: true
})
export class ResizableDirective implements OnInit, OnDestroy, OnChanges {
	@Input() resizableDirections: string[] = ['top', 'bottom', 'left', 'right'];
	@Input() resizable: boolean = true;
	@Input() maxHeight: number = -1;
	@Input() clickElementId: string = '';

	private isResizing = false;
	private startY: number = 0;
	private startHeight: number = 0;
	private overlay: HTMLElement | null = null;
	private rafId: number | null = null;
	private resizeHandle: HTMLElement | null = null;

	constructor(private el: ElementRef, private renderer: Renderer2, private ngZone: NgZone) { }

	ngOnInit() {
		// Check if the host element's position is 'static' (the default)
		const position = getComputedStyle(this.el.nativeElement).position;
		if (position === 'static' || !position) {
			// Set the position to 'relative' to create a positioning context
			this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
		}

		if (this.resizableDirections.includes('top')) {
			this.createResizeHandle();
		}
	}

	ngOnDestroy() {
		this.removeOverlay();
		this.removeResizeHandle();
		if (this.rafId !== null) {
			cancelAnimationFrame(this.rafId);
		}
	}

	ngOnChanges(): void {
		if (this.resizable) {
			this.createResizeHandle();
		}
		else {
			this.removeResizeHandle();
		}
	}

	private createResizeHandle() {
		if (!this.resizeHandle) {
			this.resizeHandle = this.renderer.createElement('div');
			this.renderer.addClass(this.resizeHandle, 'resize-handle');
			this.renderer.setStyle(this.resizeHandle, 'position', 'absolute');
			this.renderer.setStyle(this.resizeHandle, 'top', '0');
			this.renderer.setStyle(this.resizeHandle, 'left', '0');
			this.renderer.setStyle(this.resizeHandle, 'width', '100%');
			this.renderer.setStyle(this.resizeHandle, 'height', '10px');
			this.renderer.setStyle(this.resizeHandle, 'cursor', 'ns-resize');
			this.renderer.setStyle(this.resizeHandle, 'z-index', '1000');

			this.renderer.appendChild(this.el.nativeElement, this.resizeHandle);

			this.renderer.listen(this.resizeHandle, 'mousedown', this.onMouseDown.bind(this));
		}
	}

	private removeResizeHandle() {
		if (this.resizeHandle) {
			this.renderer.removeChild(this.el.nativeElement, this.resizeHandle);
			this.resizeHandle = null;
		}
	}

	private onMouseDown(event: MouseEvent) {
		if (event.button !== 0) return; // Only respond to left-click		
		if (this.exceedMaxHeight())
			return;
		if (this.resizable && this.resizableDirections.includes('top')) {
			this.isResizing = true;
			this.startY = event.clientY;
			this.startHeight = this.el.nativeElement.offsetHeight;
			this.createOverlay();
			event.preventDefault();

			this.ngZone.runOutsideAngular(() => {
				document.addEventListener('mousemove', this.onMouseMove);
				document.addEventListener('mouseup', this.onMouseUp);
			});
		}
	}

	private onMouseMove = (event: MouseEvent) => {
		if (this.exceedMaxHeight())
			return;
		if (this.isResizing) {
			if (this.clickElementId) {
				const clickElement = this.el.nativeElement.querySelector(`#${this.clickElementId}`);
				if (clickElement)
					clickElement.click();
			}
			if (this.rafId !== null) {
				cancelAnimationFrame(this.rafId);
			}
			this.rafId = requestAnimationFrame(() => {
				const diffY = this.startY - event.clientY;
				const newHeight = this.startHeight + diffY;
				this.renderer.setStyle(this.el.nativeElement, 'height', `${newHeight}px`);
			});
		}
	}

	private onMouseUp = () => {
		if (this.exceedMaxHeight())
			return;
		if (this.isResizing) {
			this.isResizing = false;
			this.removeOverlay();
			document.removeEventListener('mousemove', this.onMouseMove);
			document.removeEventListener('mouseup', this.onMouseUp);
			if (this.rafId !== null) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
		}
	}

	private createOverlay() {
		this.overlay = this.renderer.createElement('div');
		this.renderer.setStyle(this.overlay, 'position', 'fixed');
		this.renderer.setStyle(this.overlay, 'top', '0');
		this.renderer.setStyle(this.overlay, 'left', '0');
		this.renderer.setStyle(this.overlay, 'right', '0');
		this.renderer.setStyle(this.overlay, 'bottom', '0');
		this.renderer.setStyle(this.overlay, 'background', 'transparent');
		this.renderer.setStyle(this.overlay, 'z-index', '9999');
		this.renderer.appendChild(document.body, this.overlay);
	}

	private removeOverlay() {
		if (this.overlay) {
			this.renderer.removeChild(document.body, this.overlay);
			this.overlay = null;
		}
	}

	private exceedMaxHeight(): boolean {
		if (this.el?.nativeElement && this.maxHeight !== -1) {
			if (this.el.nativeElement.offsetHeight > this.maxHeight)
				return true;
		}
		return false;
	}
}
